// src/modules/perfil/PerfilTurmasDashboard.js
import React, { useState, useEffect, useMemo } from 'react';
import { db, paths } from '../../firebase/config';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList
} from 'recharts';

// --- CONFIGURAÇÕES VISUAIS ---
const COLORS_GENDER = ['#002060', '#00B0F0']; // Escuro (Fem), Claro (Masc)
const COLORS_ORIGIN = ['#00B0F0', '#002060']; // Civil (Claro), PM (Escuro)
const COLORS_AGE_PIE = ['#5B9BD5', '#ED7D31', '#A5A5A5', '#FFC000', '#4472C4', '#70AD47'];

// --- FUNÇÕES AUXILIARES ---

const normalizeText = (text) => {
    if (!text) return "";
    return text.toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase().trim();
};

const getYear = (dateString) => {
    if (!dateString) return null;
    try {
        const cleanDate = dateString.toString().replace(/\s/g, '');
        if (cleanDate.includes('-')) return parseInt(cleanDate.split('-')[0]);
        if (cleanDate.includes('/')) return parseInt(cleanDate.split('/')[2]);
        const dateObj = new Date(cleanDate);
        if (!isNaN(dateObj.getFullYear())) return dateObj.getFullYear();
        return null;
    } catch { return null; }
};

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-2 border border-gray-300 shadow-md text-xs z-50 text-gray-800">
          <p className="font-bold">{label || payload[0].name}</p>
          <p>Qtd: {payload[0].value}</p>
        </div>
      );
    }
    return null;
};

export const PerfilTurmasDashboard = () => {
  const [cadetes, setCadetes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapaTurmas, setMapaTurmas] = useState({});
  const [turmasDisponiveis, setTurmasDisponiveis] = useState([]);
  const [turmaSelecionadaId, setTurmaSelecionadaId] = useState('');

  useEffect(() => {
    const fetchDados = async () => {
      try {
        setLoading(true);
        const turmasSnap = await getDocs(collection(db, paths.turmas));
        const turmasMap = {};
        const listaTurmas = [];
        turmasSnap.forEach(doc => {
            const data = doc.data();
            if (data.nome) {
                turmasMap[doc.id] = data.nome;
                listaTurmas.push({ id: doc.id, nome: data.nome });
            }
        });
        listaTurmas.sort((a, b) => a.nome.localeCompare(b.nome));
        setMapaTurmas(turmasMap);
        setTurmasDisponiveis(listaTurmas);

        if (listaTurmas.length > 0) {
        // 1. Tenta encontrar exatamente "Aspirantes 2027"
        const turmaPreferencial = listaTurmas.find(t => t.nome === "Aspirantes 2027");
    
        if (turmaPreferencial) {
        setTurmaSelecionadaId(turmaPreferencial.id);
        } else {
        // 2. Se não achar, pega a PRIMEIRA da lista (índice 0) em vez da última.
        // Como a lista é alfabética, "Aspirantes" geralmente será o primeiro.
        setTurmaSelecionadaId(listaTurmas[0].id);
         }
        }
        const q = query(collection(db, paths.users), where("nivel", "==", "cadete"));
        const snapshot = await getDocs(q);
        const listaCadetes = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
                id: doc.id,
                ...data,
                turma_id: data.turma_id ?? null,
                genero: normalizeText(data.genero || data.sexo || "N/I"),
                nascimento: data.data_nascimento || data.nascimento || null,
                // CORREÇÃO: Adicionado leitura do campo 'vinculo'
                vinculo: normalizeText(data.vinculo), 
                origem: normalizeText(data.origem_ingresso || data.forma_ingresso || data.origem || "CIVIL"),
                data_posse: data.data_posse || data.posse || data.posse_pm || data.data_inclusao || null
            };
        });
        setCadetes(listaCadetes);
      } catch (error) {
        console.error("Erro ao carregar dados:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchDados();
  }, []);

  const dadosFiltrados = useMemo(() => {
    if (!turmaSelecionadaId) return [];
    return cadetes.filter(c => c.turma_id === turmaSelecionadaId);
  }, [cadetes, turmaSelecionadaId]);

  // CORREÇÃO: Nova lógica para verificar se é PM usando o campo 'vinculo'
  const checkIsPM = (cadete) => {
      // 1. Prioridade absoluta: Se o vínculo for explicitamente CIVIL, retorna false
      if (cadete.vinculo === 'CIVIL') return false;
      
      // 2. Se o vínculo for explicitamente Militar
      if (cadete.vinculo.includes('MILITAR') || cadete.vinculo.includes('PM')) return true;

      // 3. Fallback (caso o campo vínculo esteja vazio): tenta adivinhar pelo texto da origem
      const terms = ["PM", "MILITAR", "SD", "CB", "SGT", "TEN", "SOLDADO", "CABO", "POLICIA", "AL OF"];
      const hasTerm = terms.some(term => cadete.origem.includes(term));
      
      // 4. Fallback final: Se tiver data de posse antiga, assumimos PM, a menos que tenhamos certeza que é civil
      const hasPosseDate = !!cadete.data_posse && cadete.data_posse.trim() !== "";
      
      return hasTerm || hasPosseDate;
  };

  const getIngressoDate = (cadete) => {
      return getYear(cadete.data_posse);
  };

  const dadosGenero = useMemo(() => {
    let masc = 0;
    let fem = 0;
    dadosFiltrados.forEach(c => {
        if (c.genero.startsWith('F') || c.genero.includes('MULHER')) fem++;
        else masc++;
    });
    return [{ name: 'FEM', value: fem }, { name: 'MASC', value: masc }].filter(d => d.value > 0);
  }, [dadosFiltrados]);

  const dadosOrigem = useMemo(() => {
      let pm = 0;
      let civil = 0;
      dadosFiltrados.forEach(c => {
          if (checkIsPM(c)) pm++;
          else civil++;
      });
      return [{ name: 'CIVIL', value: civil }, { name: 'PM', value: pm }].filter(d => d.value > 0);
  }, [dadosFiltrados]);

  const { dadosIdadeGrupos, dadosAnoNascimento } = useMemo(() => {
      const groups = { 'Até 1990': 0, '1991 a 1995': 0, '1996 a 2000': 0, '2001 a 2005': 0, '2006 a 2010': 0, '2011+': 0 };
      const yearsCount = {};
      dadosFiltrados.forEach(c => {
          const ano = getYear(c.nascimento);
          if (ano) {
              yearsCount[ano] = (yearsCount[ano] || 0) + 1;
              if (ano <= 1990) groups['Até 1990']++;
              else if (ano <= 1995) groups['1991 a 1995']++;
              else if (ano <= 2000) groups['1996 a 2000']++;
              else if (ano <= 2005) groups['2001 a 2005']++;
              else if (ano <= 2010) groups['2006 a 2010']++;
              else groups['2011+']++;
          }
      });
      return { 
          dadosIdadeGrupos: Object.keys(groups).map(k => ({ name: k, value: groups[k] })).filter(d => d.value > 0),
          dadosAnoNascimento: Object.keys(yearsCount).sort().map(k => ({ name: k, value: yearsCount[k] }))
      };
  }, [dadosFiltrados]);

  const { dadosTempoServico, dadosAnoIngresso } = useMemo(() => {
      const groups = { 'Até 2010': 0, '2011 a 2015': 0, '2016 a 2020': 0, '2021 a 2025': 0 };
      const yearsCount = {};
      dadosFiltrados.forEach(c => {
          if (checkIsPM(c)) {
              const ano = getIngressoDate(c);
              if (ano) {
                  yearsCount[ano] = (yearsCount[ano] || 0) + 1;
                  if (ano <= 2010) groups['Até 2010']++;
                  else if (ano <= 2015) groups['2011 a 2015']++;
                  else if (ano <= 2020) groups['2016 a 2020']++;
                  else groups['2021 a 2025']++;
              }
          }
      });
      return { 
          dadosTempoServico: Object.keys(groups).map(k => ({ name: k, value: groups[k] })).filter(d => d.value > 0), 
          dadosAnoIngresso: Object.keys(yearsCount).sort().map(k => ({ name: k, value: yearsCount[k] }))
      };
  }, [dadosFiltrados]);

  const ChartCard = ({ title, children, className = "" }) => (
      <div className={`bg-white p-2 rounded shadow border border-gray-200 flex flex-col h-full ${className}`}>
          <h3 className="text-center font-bold text-gray-700 uppercase text-xs mb-2 pt-2">{title}</h3>
          <div className="flex-grow w-full h-64 relative">
              {children}
          </div>
      </div>
  );

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-4 bg-gray-100 min-h-screen font-sans">
       <div className="mb-6 bg-white p-4 rounded-lg shadow border border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-4">
          <h1 className="text-xl font-bold text-gray-800">
             Dashboard {mapaTurmas[turmaSelecionadaId] ? `- ${mapaTurmas[turmaSelecionadaId]}` : ''}
          </h1>
          <select 
                value={turmaSelecionadaId} 
                onChange={(e) => setTurmaSelecionadaId(e.target.value)}
                className="p-2 border border-gray-300 rounded text-sm bg-gray-50 text-gray-800 focus:ring-2 focus:ring-blue-500 outline-none"
            >
                {turmasDisponiveis.map(t => (
                    <option key={t.id} value={t.id}>{t.nome}</option>
                ))}
            </select>
       </div>

       {dadosFiltrados.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-10 text-gray-500">
            <p className="text-lg font-medium">Selecione uma turma com dados.</p>
          </div>
       ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <ChartCard title="1º CFO MASC/FEM">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie data={dadosGenero} cx="50%" cy="50%" labelLine={true} label={({ value, percent }) => `${value}; ${(percent * 100).toFixed(0)}%`} outerRadius={80} fill="#8884d8" dataKey="value">
                            {dadosGenero.map((entry, index) => (<Cell key={`cell-${index}`} fill={COLORS_GENDER[index % COLORS_GENDER.length]} />))}
                        </Pie>
                        <Legend layout="vertical" verticalAlign="middle" align="right" wrapperStyle={{fontSize: '11px', right: 0}} />
                        <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard title="1º CFO POR IDADE A CADA 5 ANOS">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie data={dadosIdadeGrupos} cx="50%" cy="50%" label={({ value, percent }) => `${value}; ${(percent * 100).toFixed(0)}%`} outerRadius={80} dataKey="value">
                            {dadosIdadeGrupos.map((entry, index) => (<Cell key={`cell-${index}`} fill={COLORS_AGE_PIE[index % COLORS_AGE_PIE.length]} />))}
                        </Pie>
                        <Legend layout="vertical" verticalAlign="middle" align="right" wrapperStyle={{fontSize: '10px', right: -10}} width={100}/>
                        <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard title="1º CFO TEMPO SV PM A CADA 5 ANOS">
                {dadosTempoServico.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie data={dadosTempoServico} cx="50%" cy="50%" label={({ value, percent }) => `${(percent * 100).toFixed(0)}%`} outerRadius={80} dataKey="value">
                                {dadosTempoServico.map((entry, index) => (<Cell key={`cell-${index}`} fill={COLORS_AGE_PIE[index % COLORS_AGE_PIE.length]} />))}
                            </Pie>
                            <Legend layout="vertical" verticalAlign="middle" align="right" wrapperStyle={{fontSize: '10px'}}/>
                            <Tooltip content={<CustomTooltip />} />
                        </PieChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 text-xs text-center px-4">
                        <p>Nenhum dado de Tempo de Serviço.</p>
                        <p className="mt-1 opacity-70">Verifique o campo 'Posse (PM)'.</p>
                    </div>
                )}
              </ChartCard>
              <ChartCard title="1º CFO PM/CIVIL">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie data={dadosOrigem} cx="50%" cy="50%" labelLine={true} label={({ value, percent }) => `${value}; ${(percent * 100).toFixed(0)}%`} outerRadius={80} dataKey="value">
                            {dadosOrigem.map((entry, index) => (<Cell key={`cell-${index}`} fill={COLORS_ORIGIN[index % COLORS_ORIGIN.length]} />))}
                        </Pie>
                        <Legend layout="vertical" verticalAlign="middle" align="right" wrapperStyle={{fontSize: '11px'}} />
                        <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard title="1º CFO POR IDADE (ANO NASC.)">
                 <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dadosAnoNascimento} margin={{ top: 20, right: 10, left: 10, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="name" tick={{fontSize: 10}} angle={-45} textAnchor="end" />
                        <Tooltip content={<CustomTooltip />} cursor={{fill: 'transparent'}} />
                        <Bar dataKey="value" fill="#5B9BD5">
                            <LabelList dataKey="value" position="top" style={{ fill: '#333', fontSize: '10px', fontWeight: 'bold' }} />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
              </ChartCard>
               <ChartCard title="1º CFO INGRESSO PM (ANO)">
                 {dadosAnoIngresso.length > 0 ? (
                     <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={dadosAnoIngresso} margin={{ top: 20, right: 10, left: 10, bottom: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="name" tick={{fontSize: 10}} angle={-45} textAnchor="end" />
                            <Tooltip content={<CustomTooltip />} cursor={{fill: 'transparent'}} />
                            <Bar dataKey="value" fill="#5B9BD5">
                                <LabelList dataKey="value" position="top" style={{ fill: '#333', fontSize: '10px', fontWeight: 'bold' }} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                 ) : (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 text-xs text-center px-4">
                        <p>Nenhum dado de Ingresso.</p>
                        <p className="mt-1 opacity-70">Verifique o campo 'Posse (PM)'.</p>
                    </div>
                 )}
              </ChartCard>
          </div>
       )}
    </div>
  );
};